import api from './api';

export interface InvestmentArea {
  id: number;
  name: string;
  platform_id: number;
  target_percentage: number;
  platform_name: string;
  platform_description: string;
  created_at: string;
  updated_at: string;
}

export interface InvestmentProduct {
  id: number;
  area_id: number;
  name: string;
  currency_id: number;
  target_percentage: number;
  enable_fall_analysis?: boolean;
  market_code?: string | null;
  three_month_variation_percent?: number | null;
  three_month_variation_direction?: 'up' | 'down' | 'stable';
  area_name: string;
  currency_code: string;
  currency_symbol: string;
  platform_name: string;
  created_at: string;
  updated_at: string;
}

export interface DataInsertion {
  id: number;
  product_id: number;
  amount: number;
  insertion_date: string;
  notes?: string;
  product_name: string;
  area_name: string;
  currency_code: string;
  currency_symbol: string;
  created_at: string;
  updated_at: string;
  is_latest?: boolean;
}

export interface DataInsertionsResponse {
  latest_by_product: DataInsertion[];
  recent_insertions: DataInsertion[];
}

export interface CapitalIncrease {
  id: number;
  total_amount: number;
  base_currency_id: number;
  increase_date: string;
  notes?: string;
  base_currency_code: string;
  base_currency_symbol: string;
  distributions?: CapitalDistribution[];
  created_at: string;
  updated_at: string;
}

export interface CreateCapitalIncreaseRequest {
  total_amount?: number;
  base_currency_id: number;
  increase_date: string;
  notes?: string;
  distributions?: Record<number, {
    product_id: number;
    amount: number;
    amount_eur: number;
  }>;
}

export interface CapitalDistribution {
  id: number;
  capital_increase_id: number;
  product_id: number;
  allocated_amount: number;
  product_name: string;
  area_name: string;
  currency_code: string;
  currency_symbol: string;
  created_at: string;
}

export interface ProductEvolution {
  product_id: number;
  product_name: string;
  area_name: string;
  evolution: Array<{
    date: string;
    value_eur: number;
    variation_percent: number;
  }>;
}

export interface PortfolioVsMarketProduct {
  product_id: number;
  product_name: string;
  area_name: string;
  market_code: string;
  evolution: Array<{
    date: string;
    portfolio_variation_percent: number;
    market_variation_percent: number | null;
  }>;
}

export interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  created_at: string;
}

export interface Platform {
  id: number;
  name: string;
  description?: string;
  created_at: string;
}

export interface ProductDistribution {
  product_id: number;
  product_name: string;
  area_name: string;
  area_target: number;
  product_target: number;
  currency_code: string;
  current_amount: number;
  last_reading_date: string;
  total_readings: number;
  current_value_eur: number;
  current_percentage: number;
  target_percentage: number;
  deviation: number;
  variation_percent?: number;
  variation_direction?: string;
  total_invested_eur?: number;
  profit_eur?: number;
  return_percentage?: number;
}

export interface ProfitLoss {
  area_name: string;
  product_name: string;
  total_invested_eur: number;
  number_of_investments: number;
  first_investment: string;
  last_investment: string;
}

export interface PerformanceIndicators {
  total_return: number;
  annualized_return: number;
  volatility: number;
  sharpe_ratio: number;
  max_drawdown: number;
  best_day: number;
  worst_day: number;
}


export interface RecentInvestment {
  id: number;
  total_amount: number;
  increase_date: string;
  notes: string;
  base_currency_code: string;
  base_currency_symbol: string;
  distributed_products: number;
  formatted_date: string;
  formatted_amount: string;
}

export interface InvestedByProduct {
  product_id: number;
  product_name: string;
  area_name: string;
  product_currency_code: string;
  product_currency_symbol: string;
  total_invested_eur: number;
  number_of_investments: number;
  first_investment: string;
  last_investment: string;
  formatted_first_date: string;
  formatted_last_date: string;
}

export interface ProductPerformance {
  product_id: number;
  product_name: string;
  area_name: string;
  currency_code: string;
  currency_symbol: string;
  variation_percent: number;
  variation_direction: 'up' | 'down' | 'stable';
  current_value_eur: number;
  invested_value_eur: number;
  absolute_gain_loss_eur: number;
  enable_fall_analysis?: boolean;
  market_code?: string | null;
  fall_analysis?: {
    drop_percent: number;
    current_price: number;
    past_price: number;
    current_date: string | null;
    past_date: string | null;
    reinforcement_weekly_range: {
      min: number;
      max: number;
    } | null;
  } | null;
}

export interface RiskAnalysis {
  herfindahl_index: number;
  diversification_score: number;
  risk_level: string;
  concentration_analysis: Array<{
    area_name: string;
    product_count: number;
    area_value_eur: number;
  }>;
  recommendations: Array<{
    type: string;
    message: string;
    priority: string;
  }>;
}

export interface AutomaticInvestment {
  id: number;
  product_id: number;
  amount: number;
  start_date: string;
  frequency: 'weekly' | 'monthly';
  day_of_week?: number; // 1=Segunda, 2=Terça, 3=Quarta, 4=Quinta, 5=Sexta, 6=Sábado, 7=Domingo
  day_of_month?: number | null; // 1-31 ou null para final do mês
  is_active: boolean;
  notes?: string;
  product_name: string;
  area_name: string;
  platform_name: string;
  currency_code: string;
  currency_symbol: string;
  created_at: string;
  updated_at: string;
}

export interface DashboardOverview {
  portfolio: {
    total_value_eur: number;
    investment_count: number;
    active_products: number;
    last_investment?: {
      insertion_date: string;
      amount: number;
      product_name: string;
      currency_code: string;
    };
    last_capital_increase?: {
      increase_date: string;
      total_amount: number;
      currency_code: string;
      currency_symbol: string;
    };
    growth_data: {
      total_growth_eur: number;
      total_growth_percentage: number;
      by_product: Array<{
        product_id: number;
        product_name: string;
        area_name: string;
        currency_code: string;
        current_amount: number;
        current_date: string;
        previous_amount: number;
        previous_date: string;
        current_value_eur: number;
        previous_value_eur: number;
        growth_eur: number;
        growth_percentage: number;
      }>;
    };
    investment_data: {
      total_current_investment_eur: number;
      total_previous_investment_eur: number;
      total_investment_change_eur: number;
      total_investment_change_percentage: number;
      by_product: Array<{
        product_id: number;
        product_name: string;
        area_name: string;
        currency_code: string;
        current_investment: number;
        current_investment_date: string;
        previous_investment: number;
        previous_investment_date: string;
        current_investment_eur: number;
        previous_investment_eur: number;
        investment_change_eur: number;
        investment_change_percentage: number;
      }>;
    };
    distribution_data: {
      total_value_eur: number;
      by_area: Array<{
        area_id: number;
        area_name: string;
        target_percentage: number;
        current_value_eur: number;
        current_percentage: number;
        deviation_percentage: number;
        products: Array<{
          product_id: number;
          product_name: string;
          target_percentage: number;
          current_value_eur: number;
          current_percentage: number;
          deviation_percentage: number;
          currency_code: string;
        }>;
      }>;
      by_product: Array<{
        product_id: number;
        product_name: string;
        area_name: string;
        target_percentage: number;
        current_value_eur: number;
        current_percentage: number;
        currency_code: string;
      }>;
    };
    drawdown?: {
      max_drawdown_percentage: number;
      current_drawdown_percentage: number;
      peak_value_eur: number;
      max_growth_percentage?: number;
      min_growth_percentage?: number;
    };
    annualized_return?: {
      annualized_return_percentage: number;
      total_invested_eur: number;
      years: number;
      has_data: boolean;
    };
  };
  evolution: Array<{
    date: string;
    total_value_eur: number;
  }>;
  area_distribution: Array<{
    area_name: string;
    target_percentage: number;
    current_value_eur: number;
    current_percentage: number;
    deviation: number;
    number_of_products: number;
    total_investments: number;
    variation_percent?: number;
    variation_direction?: string;
  }>;
  lazy_endpoints: {
    profit_loss: string;
    product_distribution: string;
    performance_indicators: string;
    rebalancing_data: string;
    risk_analysis: string;
  };
}

export interface DashboardDiagnostics {
  automatic_investments: {
    last_processed_date: string | null;
    processed_today: boolean;
    processed_today_count: number;
  };
  cache: {
    ttl_seconds: number;
    ttl_hours: number;
    last_invalidation_at: string | null;
    active_cache_files: number;
  };
  market_data_api: {
    configured: boolean;
    configured_value: string | null;
    effective_url: string;
    is_reachable: boolean;
    status: 'ok' | 'error';
    error: string | null;
  };
  server_time: string;
}

// Investment Areas
export const getInvestmentAreas = async (): Promise<InvestmentArea[]> => {
  const response = await api.get('/areas');
  return response.data.areas;
};

export const getInvestmentArea = async (id: number): Promise<InvestmentArea> => {
  const response = await api.get(`/areas/${id}`);
  return response.data;
};

export const createInvestmentArea = async (data: Partial<InvestmentArea>): Promise<{ id: number; message: string }> => {
  const response = await api.post('/areas', data);
  return response.data;
};

export const updateInvestmentArea = async (id: number, data: Partial<InvestmentArea>): Promise<{ message: string }> => {
  const response = await api.put(`/areas/${id}`, data);
  return response.data;
};

export const deleteInvestmentArea = async (id: number): Promise<{ message: string }> => {
  const response = await api.delete(`/areas/${id}`);
  return response.data;
};

// Investment Products
export const getInvestmentProducts = async (): Promise<InvestmentProduct[]> => {
  const response = await api.get('/products');
  return response.data;
};

export const getInvestmentProductsByArea = async (areaId: number): Promise<{
  products: InvestmentProduct[];
  total_percentage: number;
  is_balanced: boolean;
}> => {
  const response = await api.get(`/products/area/${areaId}`);
  return response.data;
};

export const getInvestmentProduct = async (id: number): Promise<InvestmentProduct> => {
  const response = await api.get(`/products/${id}`);
  return response.data;
};

export const createInvestmentProduct = async (data: Partial<InvestmentProduct>): Promise<{ id: number; message: string }> => {
  const response = await api.post('/products', data);
  return response.data;
};

export const updateInvestmentProduct = async (id: number, data: Partial<InvestmentProduct>): Promise<{ message: string }> => {
  const response = await api.put(`/products/${id}`, data);
  return response.data;
};

export const deleteInvestmentProduct = async (id: number): Promise<{ message: string }> => {
  const response = await api.delete(`/products/${id}`);
  return response.data;
};

// Data Insertions
export const getDataInsertions = async (): Promise<DataInsertionsResponse> => {
  const response = await api.get('/insertions');
  return response.data;
};

export const getDataInsertionsByProduct = async (productId: number): Promise<DataInsertion[]> => {
  const response = await api.get(`/insertions/product/${productId}`);
  return response.data;
};

export const getDataInsertion = async (id: number): Promise<DataInsertion> => {
  const response = await api.get(`/insertions/${id}`);
  return response.data;
};

export const createDataInsertion = async (data: Partial<DataInsertion>): Promise<{ id: number; message: string }> => {
  const response = await api.post('/insertions', data);
  return response.data;
};

export const updateDataInsertion = async (id: number, data: Partial<DataInsertion>): Promise<{ message: string }> => {
  const response = await api.put(`/insertions/${id}`, data);
  return response.data;
};

export const deleteDataInsertion = async (id: number): Promise<{ message: string }> => {
  const response = await api.delete(`/insertions/${id}`);
  return response.data;
};

// Capital Increases
export const getCapitalIncreases = async (): Promise<CapitalIncrease[]> => {
  const response = await api.get('/capital-increases');
  return response.data;
};

export const getCapitalIncrease = async (id: number): Promise<CapitalIncrease> => {
  const response = await api.get(`/capital-increases/${id}`);
  return response.data;
};

export const createCapitalIncrease = async (data: CreateCapitalIncreaseRequest): Promise<{
  id: number;
  message: string;
  distribution?: Record<number, any>;
}> => {
  const response = await api.post('/capital-increases', data);
  return response.data;
};

export const updateCapitalIncrease = async (id: number, data: Partial<CapitalIncrease>): Promise<{ message: string }> => {
  const response = await api.put(`/capital-increases/${id}`, data);
  return response.data;
};

export const deleteCapitalIncrease = async (id: number): Promise<{ message: string }> => {
  const response = await api.delete(`/capital-increases/${id}`);
  return response.data;
};

export const calculateDistribution = async (data: {
  total_amount: number;
  base_currency_id: number;
}): Promise<Record<number, any>> => {
  const response = await api.post('/capital-increases/calculate-distribution', data);
  return response.data;
};

// Dashboard
export const getDashboardOverview = async (): Promise<DashboardOverview> => {
  const response = await api.get('/dashboard/overview');
  return response.data;
};

export const getDashboardDiagnostics = async (): Promise<DashboardDiagnostics> => {
  const response = await api.get('/dashboard/diagnostics');
  return response.data;
};

export const getPortfolioEvolution = async (): Promise<Array<{ date: string; total_value_eur: number }>> => {
  const response = await api.get('/dashboard/evolution');
  return response.data;
};

export const getProductEvolution = async (): Promise<ProductEvolution[]> => {
  const response = await api.get('/dashboard/product-evolution');
  return response.data;
};

export const getPortfolioVsMarket = async (): Promise<PortfolioVsMarketProduct[]> => {
  const response = await api.get('/dashboard/portfolio-vs-market');
  return response.data;
};

export const getProfitLoss = async (): Promise<Array<{
  area_name: string;
  product_name: string;
  total_invested_eur: number;
  number_of_investments: number;
  first_investment: string;
  last_investment: string;
}>> => {
  const response = await api.get('/dashboard/profit-loss');
  return response.data;
};

export const getAreaDistribution = async (): Promise<Array<{
  area_name: string;
  target_percentage: number;
  current_value_eur: number;
  current_percentage: number;
  deviation: number;
  number_of_products: number;
  total_investments: number;
}>> => {
  const response = await api.get('/dashboard/area-distribution');
  return response.data;
};

// Utilities
export const getCurrencies = async (): Promise<Currency[]> => {
  const response = await api.get('/currencies');
  return response.data;
};

export const getPlatforms = async (): Promise<Platform[]> => {
  const response = await api.get('/platforms');
  return response.data;
};

// Automatic Investments
export const getAutomaticInvestments = async (): Promise<AutomaticInvestment[]> => {
  const response = await api.get('/automatic-investments');
  return response.data;
};

export const getAutomaticInvestment = async (id: number): Promise<AutomaticInvestment> => {
  const response = await api.get(`/automatic-investments/${id}`);
  return response.data;
};

export const createAutomaticInvestment = async (data: Partial<AutomaticInvestment>): Promise<{ id: number; message: string }> => {
  const response = await api.post('/automatic-investments', data);
  return response.data;
};

export const updateAutomaticInvestment = async (id: number, data: Partial<AutomaticInvestment>): Promise<{ message: string }> => {
  const response = await api.put(`/automatic-investments/${id}`, data);
  return response.data;
};

export const deleteAutomaticInvestment = async (id: number): Promise<{ message: string }> => {
  const response = await api.delete(`/automatic-investments/${id}`);
  return response.data;
};

export const processAutomaticInvestments = async (): Promise<{
  success: boolean;
  message: string;
  data: {
    processed: number;
    total: number;
    errors: string[];
  };
}> => {
  const response = await api.post('/automatic-investments/process');
  return response.data;
};
